import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const cardVariants = cva('rounded-xl border bg-bg-elevated border-border-default transition-all duration-200', {
  variants: {
    elevation: {
      flat: 'shadow-none',
      soft: 'shadow-soft dark:shadow-none',
      raised: 'shadow-md dark:shadow-none',
    },
    interactive: {
      // `w-full text-left` because an interactive card renders as a <button>,
      // which would otherwise shrink-wrap and centre its content. The
      // `disabled:` utilities match Button's convention — `pointer-events-none`
      // also suppresses the hover styles, which would otherwise still paint on
      // a disabled card.
      true: 'w-full cursor-pointer text-left hover:shadow-soft-lg hover:border-border-strong dark:hover:bg-bg-elevated/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base disabled:pointer-events-none disabled:opacity-50',
      false: '',
    },
    selected: {
      true: 'border-border-accent bg-bg-accent-muted',
      false: '',
    },
  },
  defaultVariants: {
    elevation: 'soft',
    interactive: false,
    selected: false,
  },
});

export { cardVariants };

// HTMLElement, not HTMLDivElement: an interactive card is a <button>. The
// shared attributes (onClick, aria-*, data-*) are the ones consumers pass.
interface CardProps extends HTMLAttributes<HTMLElement>, VariantProps<typeof cardVariants> {
  children: ReactNode;
  /**
   * Only meaningful with `interactive`, where the card is a real `<button>` —
   * it is not forwarded to a plain card, because `disabled` is not a valid
   * attribute on a `<div>` and React would emit it into the DOM regardless.
   *
   * Declared explicitly rather than widening the whole prop type to
   * `ButtonHTMLAttributes`: the other button-only attributes (`form`,
   * `formAction`, `value`) have no meaning for a card, and a discriminated
   * union on `interactive` costs every call site a narrowing.
   */
  disabled?: boolean;
}

/**
 * A surface. Pass `interactive` when the whole card is one activation target —
 * it then renders as a real `<button>`, so keyboard focus, Enter/Space and the
 * button role come from the platform rather than being re-implemented.
 *
 * Because of that, an interactive card must NOT contain its own buttons or
 * links: nesting interactive elements inside a button is invalid and
 * unreachable by keyboard. For a card that carries its own actions, leave it
 * non-interactive and make one child the activation target.
 *
 * `selected` marks the current item in a set — it sets `aria-current`, so it
 * reads as chosen rather than merely looking different.
 */
export function Card({ className, children, elevation, interactive, selected, disabled, ...props }: CardProps) {
  const classes = cn(cardVariants({ elevation, interactive, selected }), className);

  if (interactive) {
    return (
      <button
        type="button"
        className={classes}
        disabled={disabled}
        aria-current={selected ? 'true' : undefined}
        {...props}
      >
        {children}
      </button>
    );
  }

  // `disabled` is deliberately dropped here, not spread — see the prop's doc.
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardHeader({ className, children, ...props }: CardHeaderProps) {
  return (
    <div className={cn('border-b border-border-default px-6 py-4', className)} {...props}>
      {children}
    </div>
  );
}

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
}

export function CardTitle({ className, children, ...props }: CardTitleProps) {
  return (
    <h3 className={cn('text-lg font-semibold text-text-primary', className)} {...props}>
      {children}
    </h3>
  );
}

const cardContentVariants = cva('', {
  variants: {
    variant: {
      default: 'p-6',
      flush: 'p-0',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export { cardContentVariants };

interface CardContentProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardContentVariants> {
  children: ReactNode;
}

export function CardContent({ className, variant, children, ...props }: CardContentProps) {
  return (
    <div className={cn(cardContentVariants({ variant }), className)} {...props}>
      {children}
    </div>
  );
}
